import { useState } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [selectedCategory, setSelectedCategory] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleGoClick = () => {
    if (selectedCategory) {
      window.open(selectedCategory, "_blank", "noopener,noreferrer");
    }
  };

  const handleMobileCategoryClick = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
    setMobileMenuOpen(false);
  };

  const navLinks = [
    { label: "Home", href: "/", isRoute: true },
    { label: "Spreadsheet", href: "#hero" },
    { label: "QC", href: "#comparison" },
    { label: "Guides", href: "#how-it-works" },
    { label: "Shopping Guide", href: "/article/vigorbuy-sheet-guide", isRoute: true },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background-50 border-b-2 border-background-200 shadow-sm">
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <span className="font-heading text-xl font-bold tracking-tight text-foreground-950">
              VIGORBUY
            </span>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) =>
              link.isRoute ? (
                <Link
                  key={link.label}
                  to={link.href}
                  className="text-sm font-semibold text-foreground-800 hover:text-accent-600 transition-colors duration-200 whitespace-nowrap"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.label}
                  href={link.href}
                  className="text-sm font-semibold text-foreground-800 hover:text-accent-600 transition-colors duration-200 whitespace-nowrap"
                >
                  {link.label}
                </a>
              )
            )}
          </div>

          {/* Category Dropdown */}
          <div className="hidden md:flex items-center gap-2">
            <div className="relative">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="appearance-none bg-background-100 border-2 border-background-300 rounded-lg px-4 py-2 pr-10 text-sm font-medium text-foreground-800 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-200 cursor-pointer min-w-[180px]"
              >
                <option value="">Browse Category</option>
                {categories.map((cat) => (
                  <option key={cat.seq} value={cat.url}>
                    {cat.name}
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-500 pointer-events-none"></i>
            </div>
            <button
              onClick={handleGoClick}
              disabled={!selectedCategory}
              className="px-4 py-2 bg-primary-500 text-background-50 text-sm font-semibold rounded-lg hover:bg-primary-600 transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer shadow-sm"
            >
              Go
            </button>
          </div>

          {/* CTA Buttons - Desktop */}
          <div className="hidden md:flex items-center gap-3 ml-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 bg-accent-500 text-background-50 text-sm font-bold rounded-full hover:bg-accent-600 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-sm"
            >
              VigorBuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 border-2 border-primary-500 text-primary-600 text-sm font-bold rounded-full hover:bg-primary-500 hover:text-background-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-800 cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-2xl font-bold ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-4 border-t-2 border-background-200 pt-4">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) =>
                link.isRoute ? (
                  <Link
                    key={link.label}
                    to={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-lg transition-colors"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-lg transition-colors"
                  >
                    {link.label}
                  </a>
                )
              )}

              {/* Mobile Category Section */}
              <div className="mt-3 pt-3 border-t border-background-200">
                <p className="px-3 text-xs font-semibold uppercase tracking-wider text-foreground-500 mb-2">
                  Product Categories
                </p>
                <div className="grid grid-cols-2 gap-1.5 px-1">
                  {categories.map((cat) => (
                    <button
                      key={cat.seq}
                      onClick={() => handleMobileCategoryClick(cat.url)}
                      className="flex items-center gap-2 px-3 py-2.5 text-left text-sm font-medium text-foreground-700 hover:bg-accent-50 hover:text-accent-700 rounded-lg transition-colors cursor-pointer"
                    >
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className={`${cat.icon} text-xs text-accent-500`}></i>
                      </span>
                      <span className="truncate">{cat.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Mobile CTA */}
              <div className="flex gap-2 pt-3 mt-3 border-t border-background-200">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex-1 text-center px-4 py-2.5 bg-accent-500 text-background-50 text-sm font-bold rounded-full hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer shadow-sm"
                >
                  VigorBuy Sheet
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex-1 text-center px-4 py-2.5 border-2 border-primary-500 text-primary-600 text-sm font-bold rounded-full hover:bg-primary-500 hover:text-background-50 transition-all whitespace-nowrap cursor-pointer"
                >
                  Buy It Now
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}